import { AuthPayload, LoginResponse } from "../apis";
import { getAccessToken } from "../auth/tokenFunctions";
import { getRefreshTokenFromStorage } from "../auth/tokenStorage";
import { getAxiosApi, getAxiosInstance } from "../axios-instance";
import { Language } from "../state/preferencesStore";

export type UserAccount = {
  accountId: number;
  accountName: string;
  isHomeAccount: boolean;
  isCurrent: boolean;
  roles: string[];
};

export function authLoginApi(data: AuthPayload) {
  return getAxiosApi()
    .post<LoginResponse>("/auth/login", data)
    .then((res) => res.data)
    .catch((err) => {
      // If backend indicates email verification is required, mark the original error
      const requiresEmailVerification = err?.response?.data?.requiresEmailVerification;
      if (requiresEmailVerification) {
        (err as any).requiresEmailVerification = true;
      }
      // Preserve the original Axios error for caller handling
      throw err;
    });
}
type SignUpRequest = {
  email: string;
  password: string;
  language?: Language;
};

export async function authRegisterWithPasswordApi(data: SignUpRequest) {
  return getAxiosApi()
    .post<{ message: string }>("/auth/register", data)
    .then((res) => res.data)
    .catch((err) => {
      // forward server message when available
      const message = err?.response?.data?.message;
      throw new Error(
        message || "Registration failed. Please check your input."
      );
    });
}
export async function authRegisterWithoutPasswordApi(data: {
  email: string;
  language?: Language;
}) {
  return getAxiosApi()
    .post<{ message: string }>("/auth/register/otp", data)
    .then((res) => res.data)
    .catch((err) => {
      // forward server message when available
      const message = err?.response?.data?.message;
      throw new Error(
        message || "Registration failed. Please check your input."
      );
    });
}
export async function authLogoutApi() {
  const accessToken = await getAccessToken();
  const refreshToken = getRefreshTokenFromStorage();

  const payload = {
    accessToken,
    refreshToken,
  };
  return getAxiosApi()
    .post<void>("/auth/logout", payload)
    .catch(() => {
      throw new Error("Logout failed.");
    });
}
export function authRefreshApi(data: {
  accessToken: string | null;
  refreshToken: string | null;
}) {
  return getAxiosApi()
    .post<LoginResponse>("/auth/refresh", data)
    .then((res) => res.data);
}

export function authChangePasswordApi(data: {
  token: string;
  currentPassword: string;
  newPassword: string;
}) {
  return getAxiosApi()
    .post<{ message: string }>(
      "/auth/change-password",
      { currentPassword: data.currentPassword, newPassword: data.newPassword },
      { headers: { Authorization: `Bearer ${data.token}` } }
    )
    .then((res) => res.data)
    .catch(() => {
      throw new Error("Password change failed. Please check your credentials.");
    });
}

export function requestPasswordResetApi(data: { email: string }) {
  return getAxiosApi()
    .post<{ message: string }>("/auth/password-reset/request", data)
    .then((res) => res.data)
    .catch((err) => {
      const message = err?.response?.data?.message;
      throw new Error(message || "Failed to request a password reset.");
    });
}

export function resetPasswordApi(data: { token: string; newPassword: string }) {
  return getAxiosApi()
    .post<{ message: string }>("/auth/password-reset", data)
    .then((res) => res.data)
    .catch((err) => {
      const message = err?.response?.data?.message;
      throw new Error(
        message || "Password reset failed. The link may be invalid or expired."
      );
    });
}

export type MyProfile = {
  accountId: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  /** The user's own language override; null means "follow the account". */
  language: Language | null;
  /** What the user should actually see: their override, or the account's language. */
  effectiveLanguage: Language;
  /** False for passkey/magic-link-only users, who have no password to change. */
  hasPassword: boolean;
};

export type ChangeMyPasswordRequest = {
  currentPassword: string;
  newPassword: string;
  /** Ends every other device's session. Defaults to true on the server. */
  signOutOtherSessions: boolean;
};

export type ChangeMyPasswordResponse = {
  message: string;
  accessToken: string;
  refreshToken: string;
};

export type ChangeMyPasswordErrorCode = "invalid_current_password" | "generic";

export class ChangeMyPasswordError extends Error {
  constructor(readonly code: ChangeMyPasswordErrorCode) {
    super(code);
    this.name = "ChangeMyPasswordError";
  }
}

/**
 * Changes the signed-in user's own password. Every token issued before the change
 * stops working — including this tab's — so the server hands back a fresh pair that
 * the caller must store (see useChangeMyPassword).
 */
export async function changeMyPasswordApi(data: ChangeMyPasswordRequest) {
  // Deliberately the raw instance: the authenticated one treats any 401 as an
  // expired token and silently refreshes + replays the request, which would turn
  // a "wrong current password" answer into a second submit of the same change.
  const token = await getAccessToken();

  return getAxiosApi()
    .post<ChangeMyPasswordResponse>(
      "/auth/change-password",
      { ...data, refreshToken: getRefreshTokenFromStorage() },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then((res) => res.data)
    .catch((err) => {
      const status = err?.response?.status;
      throw new ChangeMyPasswordError(
        status === 401 ? "invalid_current_password" : "generic"
      );
    });
}

export function getMyProfileApi() {
  return getAxiosInstance()
    .get<MyProfile>("/auth/me")
    .then((res) => res.data);
}

export type UpdateMyNameRequest = {
  firstName?: string;
  lastName?: string;
};

export function updateMyNameApi(data: UpdateMyNameRequest) {
  return getAxiosInstance()
    .put<Pick<MyProfile, "email" | "firstName" | "lastName">>("/auth/me", data)
    .then((res) => res.data);
}

export type MyLanguage = Pick<MyProfile, "language" | "effectiveLanguage">;

/** Passing null clears the override and puts the user back on the account's language. */
export function updateMyLanguageApi(data: { language: Language | null }) {
  return getAxiosInstance()
    .put<MyLanguage>("/auth/me/language", data)
    .then((res) => res.data);
}

export function getMyAccountsApi() {
  return getAxiosInstance()
    .get<UserAccount[]>("/auth/me/accounts")
    .then((res) => res.data);
}

export function switchAccountApi(data: { accountId: number }) {
  return getAxiosInstance()
    .post<LoginResponse>("/auth/switch-account", data)
    .then((res) => res.data);
}

export function requestOtpApi(data: { email: string; language?: Language }) {
  return getAxiosApi()
    .post<{ message: string; code?: string }>("/auth/otp/request", data)
    .then((res) => res.data);
}

export function verifyOtpApi(data: {
  code: string;
  createUserIfNotExists?: boolean;
}) {
  return getAxiosApi()
    .post<LoginResponse>("/auth/otp/verify", data)
    .then((res) => res.data)
    .catch((err) => {
      const message = err?.response?.data?.message;
      throw new Error(
        message || "Verification failed. Please check your code."
      );
    });
}
export function finalizeRegisterOtpApi(data: {
  code: string;
  createUserIfNotExists?: boolean;
}) {
  return verifyOtpApi({...data, createUserIfNotExists: true});
}
